import ConfigService from './Config';
import NotificationService from '../NotificationService';
import Utils from '../Utils';

// Bypass global fetch interception for ThoughtSpot SDK calls
function isThoughtSpotUrl(input) {
  try {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!url) return false;
    const tsHost = window.TS_HOST || '';
    if (tsHost && url.indexOf(tsHost) === 0) return true;
    return /thoughtspot\.cloud|callosum\//i.test(url);
  } catch (_) {
    return false;
  }
}

const constantMock = window.fetch;
window.fetch = function () {
  // If this request targets ThoughtSpot, bypass interception entirely
  if (isThoughtSpotUrl(arguments[0])) {
    return constantMock.apply(this, arguments);
  }

  return new Promise((resolve, reject) => {
    constantMock
      .apply(this, arguments)
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 503) {
            Utils.leoRedirect('maintenance');
          } else {
            const JSONresponse = await response.json();
            NotificationService.error(JSONresponse);
          }
        }
        resolve(response);
      })
      .catch((error) => {
        NotificationService.error(error);
        reject(error);
      });
  });
};

export default class BaseService {
  static endpoint = null;

  static async list(params, skipLoading) {
    const url = ConfigService.generateURL(this.endpoint, params);
    if (!skipLoading && ConfigService.contextApi) {
      ConfigService.contextApi.setLoading(true);
    }
    try {
      const response = await window.fetch(url, {
        method: 'GET',
        headers: {
          Authorization: 'jwt ' + localStorage.getItem('jwt_token'),
          'Time-Zone': jstz.determine().name()
        }
      });
      const JSONresponse = await response.json();
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if (response.ok) {
        return JSONresponse.results || JSONresponse;
      } else {
        return null;
      }
    } catch (e) {
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      return null;
    }
  }

  static async get(id, params, skipLoading) {
    const url = ConfigService.generateURL(this.endpoint + id + '/', params);
    if (!skipLoading && ConfigService.contextApi) {
      ConfigService.contextApi.setLoading(true);
    }
    try {
      const response = await window.fetch(url, {
        method: 'GET',
        headers: {
          Authorization: 'jwt ' + localStorage.getItem('jwt_token'),
          'Time-Zone': jstz.determine().name()
        }
      });
      const JSONresponse = await response.json();
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if (response.ok) {
        return JSONresponse;
      } else {
        return null;
      }
    } catch (e) {
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      return null;
    }
  }

  static async create(data, params, skipLoading) {
    const url = ConfigService.generateURL(this.endpoint, params);
    if (!skipLoading && ConfigService.contextApi) {
      ConfigService.contextApi.setLoading(true);
    }
    try {
      const response = await window.fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json, text/plain, */*',
          Authorization: 'jwt ' + localStorage.getItem('jwt_token'),
          'Content-Type': 'application/json;charset=UTF-8',
          'Time-Zone': jstz.determine().name()
        }
      });
      const JSONresponse = await response.json();
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if (response.ok) {
        NotificationService.notify('success', 'Success! Form data saved.', '', 5000);
        return JSONresponse;
      } else {
        return null;
      }
    } catch (e) {
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if(e.message === 'Failed to fetch'){
        NotificationService.notify('error', 'Oops!  No Internet Connection! \n Please check your internet settings and try again. To avoid data loss, please stay on this page until connection is restored and data is saved successfully.', '', 10000);
      }
      return null;
    }
  }

  // eslint-disable-next-line max-params
  static async update(pk, data, params, skipLoading) {
    const url = ConfigService.generateURL(this.endpoint + pk + '/', params);
    if (!skipLoading && ConfigService.contextApi) {
      ConfigService.contextApi.setLoading(true);
    }
    try {
      const response = await window.fetch(url, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json, text/plain, */*',
          Authorization: 'jwt ' + localStorage.getItem('jwt_token'),
          'Content-Type': 'application/json;charset=UTF-8',
          'Time-Zone': jstz.determine().name()
        }
      });
      const JSONresponse = await response.json();
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if (response.ok) {
        NotificationService.notify('success', 'Success! Form data saved.', '', 5000);
        return JSONresponse;
      } else {
        return null;
      }
    } catch (e) {
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if(e.message === 'Failed to fetch'){
        NotificationService.notify('error', 'Oops!  No Internet Connection! \n Please check your internet settings and try again. To avoid data loss, please stay on this page until connection is restored and data is saved successfully.', '', 10000);;
      }
      return null;
    }
  }

  static async delete(pk, skipLoading) {
    const url = ConfigService.generateURL(this.endpoint + pk + '/');
    if (!skipLoading && ConfigService.contextApi) {
      ConfigService.contextApi.setLoading(true);
    }
    try {
      const response = await window.fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: 'jwt ' + localStorage.getItem('jwt_token'),
          'Time-Zone': jstz.determine().name()
        }
      });
      const JSONresponse = await response.json();
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      if (response.ok) {
        return JSONresponse;
      } else {
        return null;
      }
    } catch (e) {
      if (!skipLoading && ConfigService.contextApi) {
        ConfigService.contextApi.setLoading(false);
      }
      return null;
    }
  }
}